import { useId, useState } from "react";
import { useApp } from "../app-context.tsx";
import { MAX_NAME } from "../config-name.ts";

export interface RenameFormProps {
  /** The user's current name, or "" when the bracket is named by its dimensions. */
  initial: string;
  /** Called with the trimmed name; "" means "name it by its dimensions again". */
  onSave: (name: string) => Promise<void> | void;
  onCancel: () => void;
  className?: string;
}

/** Inline rename: a text field with Save / Cancel; Escape cancels, an empty name goes back to the generated one. */
export function RenameForm({ initial, onSave, onCancel, className }: RenameFormProps) {
  const { copy } = useApp();
  const id = useId();
  const [value, setValue] = useState(initial);
  const [state, setState] = useState<{ busy: boolean; error: string | null }>({ busy: false, error: null });
  const submit = async () => {
    setState({ busy: true, error: null });
    try {
      await onSave(value.trim());
    } catch (err) {
      setState({ busy: false, error: (err as Error).message });
    }
  };
  return (
    <form
      className={`rename-form${className ? ` ${className}` : ""}`}
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          onCancel();
        }
      }}
    >
      <label className="cfg-visually-hidden" htmlFor={id}>
        {copy("mine.nameField")}
      </label>
      <input
        id={id}
        className="cfg-input rename-input"
        value={value}
        maxLength={MAX_NAME}
        autoFocus
        aria-describedby={`${id}-hint`}
        onChange={(e) => setValue(e.target.value)}
      />
      <button type="submit" className="cfg-btn rename-save" disabled={state.busy}>
        {copy("mine.renameSave")}
      </button>
      <button type="button" className="link rename-cancel" onClick={onCancel}>
        {copy("mine.cancel")}
      </button>
      <p className="muted small rename-hint" id={`${id}-hint`}>
        {copy("mine.renameHint")}
      </p>
      {state.error && (
        <p className="cfg-error rename-error" role="alert">
          {state.error}
        </p>
      )}
    </form>
  );
}
